import {useEffect, useState} from 'react';
import {useToast} from 'react-native-toast-notifications';
import {useDispatch, useSelector} from 'react-redux';
import {slices} from 'store';
import * as files from 'store/slices/files';
import {
  combine,
  containsFiles,
  dropTargetForElements,
  dropTargetForExternal,
  getDroppedFiles,
  isDndData,
  moveToTrash,
  moveToFolder,
} from './effects/dnd';

import type {CleanupFn} from '@atlaskit/pragmatic-drag-and-drop/types';
import type {View} from 'react-native';

interface UseDragDropTargetProps {
  ref: React.RefObject<View>;
  targetId: string;
  targetName?: string;
  options?: {
    isUploadOnly?: boolean;
    ignoreUploads?: boolean;
    queueUploads?: boolean;
  };
}

export function useDragDropTarget(props: UseDragDropTargetProps) {
  const {ref, targetId, targetName, options} = props;
  const [isDropping, setIsDropping] = useState(false);
  const selection = useSelector(files.selectors.getSelection);
  const toast = useToast();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!ref?.current) return;
    const element = ref.current as unknown as HTMLElement;
    return combine(
      ...([
        !options?.isUploadOnly &&
          dropTargetForElements({
            element,
            canDrop: ({source}) => isDndData(source.data),
            onDragEnter: () => setIsDropping(true),
            onDragLeave: () => setIsDropping(false),
            onDrop: async e => {
              setIsDropping(false);
              if (isDndData(e.source.data)) {
                const {item} = e.source.data;
                const keys = selection.length > 1 ? selection : [item.id];
                if (targetId === 'trash') {
                  toast.show(...(await moveToTrash(dispatch, keys)));
                } else {
                  toast.show(...(await moveToFolder(dispatch, keys, targetId, targetName)));
                }
              }
            },
          }),
        !options?.ignoreUploads &&
          dropTargetForExternal({
            element,
            canDrop: containsFiles,
            getDropEffect: () => 'copy',
            onDragEnter: () => setIsDropping(true),
            onDragLeave: () => setIsDropping(false),
            onDropTargetChange: ({location, self}) => {
              setTimeout(() => {
                setIsDropping(
                  location.current.dropTargets[0]?.element === self.element,
                );
              }, 1);
            },
            onDrop: e => {
              if (e.location.current.dropTargets[0]?.element !== e.self.element) {
                return;
              }
              setIsDropping(false);
              getDroppedFiles(e.source, files => {
                if (files.length) {
                  let uploadFolder = targetId;
                  dispatch(
                    slices.transfers.actions.upload({
                      id: uploadFolder,
                      files: files,
                      queue: options?.queueUploads,
                    }),
                  );
                }
              });
            },
          }),
      ].filter(Boolean) as CleanupFn[]),
    );
  }, [selection, ref?.current]);

  return {isDropping};
}
